import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Download, Loader2, RefreshCw, Users, TrendingUp, TrendingDown, AlertCircle, CreditCard, Calendar, ShoppingBag, Package, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { listSites } from '@/app/services/siteService';
import { Site } from '@/app/types/site';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  fetchCustomerLifetimeMetrics,
  fetchCustomerRetention,
  fetchCreditAnalysis,
  fetchOrderPatterns,
  fetchProductPreferences,
  CustomerLifetimeMetric,
  RetentionMetric,
  CreditAnalysis,
  OrderPattern,
  ProductPreference,
  CustomerMetricsParams,
  RetentionParams,
  CreditAnalysisParams,
  ProductPreferenceParams
} from '@/app/services/supabaseEdgeFunctionService';

const CustomerMetricsPage: React.FC = () => {
  // State management
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days
    return format(date, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [period, setPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [siteId, setSiteId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Prepare params for API calls
  const lifetimeParams: CustomerMetricsParams = {
    start_date: startDate,
    end_date: endDate,
    ...(siteId && { site_id: siteId }),
    limit: 50
  };

  const retentionParams: RetentionParams = {
    start_date: startDate,
    end_date: endDate,
    period,
    ...(siteId && { site_id: siteId })
  };

  const creditParams: CreditAnalysisParams = {
    as_of_date: endDate,
    ...(siteId && { site_id: siteId })
  };

  // Fetch sites for dropdown
  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Set the first site as default when sites are loaded
  useEffect(() => {
    if (!isInitialized && sitesData.items && sitesData.items.length > 0 && siteId === null) {
      setSiteId(sitesData.items[0].id);
      setIsInitialized(true);
    }
  }, [sitesData.items, siteId, isInitialized]);

  // State change handlers with auto-refresh
  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handlePeriodChange = (newPeriod: 'monthly' | 'quarterly' | 'yearly') => {
    setPeriod(newPeriod);
  };

  const handleSiteChange = (value: string) => {
    setSiteId(parseInt(value));
  };

  // Fetch customer lifetime metrics
  const { data: lifetimeData = [], isLoading: isLoadingLifetime, refetch: refetchLifetime } = useQuery({
    queryKey: ['customer-lifetime', lifetimeParams],
    queryFn: () => fetchCustomerLifetimeMetrics(lifetimeParams),
    enabled: !!siteId,
    retry: 1,
  });

  // Fetch retention data
  const { data: retentionData = [], isLoading: isLoadingRetention, refetch: refetchRetention } = useQuery({
    queryKey: ['customer-retention', retentionParams],
    queryFn: () => fetchCustomerRetention(retentionParams),
    enabled: !!siteId,
    retry: 1,
  });

  // Fetch credit analysis
  const { data: creditData = [], isLoading: isLoadingCredit, refetch: refetchCredit } = useQuery({
    queryKey: ['credit-analysis', creditParams],
    queryFn: () => fetchCreditAnalysis(creditParams),
    enabled: !!siteId,
    retry: 1,
  });

  // Fetch order patterns
  const { data: orderPatternData = [], isLoading: isLoadingPatterns, refetch: refetchPatterns } = useQuery({
    queryKey: ['order-patterns', lifetimeParams],
    queryFn: () => fetchOrderPatterns(lifetimeParams),
    enabled: !!siteId,
    retry: 1,
  });

  // Fetch product preferences for selected customer
  const { data: productPreferences = [], isLoading: isLoadingPreferences } = useQuery({
    queryKey: ['product-preferences', selectedCustomerId, startDate, endDate],
    queryFn: () => fetchProductPreferences({
      party_id: selectedCustomerId!,
      start_date: startDate,
      end_date: endDate,
      limit: 20
    }),
    enabled: !!selectedCustomerId,
    retry: 1,
  });

  // Combined loading state
  const isLoading = isLoadingLifetime || isLoadingRetention || isLoadingCredit || isLoadingPatterns;

  // Manual refresh function
  const handleRefresh = async () => {
    await Promise.all([
      refetchLifetime(),
      refetchRetention(),
      refetchCredit(),
      refetchPatterns()
    ]);
    toast.success('Data refreshed successfully');
  };

  // Date preset handler
  const handleDatePreset = (preset: string) => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    
    switch (preset) {
      case 'yesterday':
        handleDateChange(format(yesterday, 'yyyy-MM-dd'), format(yesterday, 'yyyy-MM-dd'));
        break;
      case 'last7days':
        handleDateChange(format(subDays(today, 6), 'yyyy-MM-dd'), format(today, 'yyyy-MM-dd'));
        break;
      case 'last30days':
        handleDateChange(format(subDays(today, 29), 'yyyy-MM-dd'), format(today, 'yyyy-MM-dd'));
        break;
      case 'thisMonth':
        handleDateChange(format(startOfMonth(today), 'yyyy-MM-dd'), format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
    }
  };

  // Export function
  const handleExport = () => {
    const csvContent = [
      // Headers
      ['Customer Metrics Report'],
      [`Period: ${startDate} to ${endDate}`],
      [''],
      
      // Customer Lifetime Metrics
      ['Customer Lifetime Metrics'],
      ['Customer Name', 'Status', 'Total Orders', 'Total Spent', 'Avg Order Value', 'Order Frequency (days)', 'Days Since Last Order', 'Outstanding'],
      ...lifetimeData.map(c => [
        c.party_name,
        c.customer_status,
        c.total_orders,
        c.total_spent,
        c.avg_order_value,
        c.order_frequency_days || 'N/A',
        c.days_since_last_order,
        c.outstanding_amount
      ]),
      [''],
      
      // Retention Metrics
      ['Retention Metrics'],
      ['Period', 'Total Customers', 'New', 'Returning', 'Lost', 'Retention Rate', 'Churn Rate', 'Avg Value'],
      ...retentionData.map(r => [
        r.period_label,
        r.total_customers,
        r.new_customers,
        r.returning_customers,
        r.lost_customers,
        `${(r.retention_rate || 0).toFixed(2)}%`,
        `${(r.churn_rate || 0).toFixed(2)}%`,
        (r.avg_customer_value || 0).toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-metrics-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Currency formatter
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Calculate summary metrics
  const activeCustomers = lifetimeData.filter(c => c.customer_status === 'Active').length;
  const totalCustomers = lifetimeData.length;
  const avgCustomerValue = lifetimeData.length > 0
    ? lifetimeData.reduce((sum, c) => sum + c.total_spent, 0) / lifetimeData.length
    : 0;
  const totalOutstanding = lifetimeData.reduce((sum, c) => sum + c.outstanding_amount, 0);
  
  const currentRetention = retentionData.length > 0 ? retentionData[retentionData.length - 1] : null;
  const retentionRate = currentRetention?.retention_rate || 0;

  // Filter customers based on search
  const filteredCustomers = lifetimeData.filter(customer =>
    customer.party_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.party_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Chart colors
  const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];

  // Status color mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'default';
      case 'At Risk': return 'secondary';
      case 'Dormant': return 'outline';
      case 'Lost': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Customer Analytics Dashboard
          </CardTitle>
          <CardDescription>
            Analyze customer behavior, retention, and lifetime value metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Quick Date Presets */}
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('yesterday')}>
              Yesterday
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('last7days')}>
              Last 7 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('last30days')}>
              Last 30 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('thisMonth')}>
              This Month
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Start Date */}
            <div className="space-y-2">
              <label htmlFor="startDate" className="text-sm font-medium">Start Date</label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange(e.target.value, endDate)}
                className="w-full"
              />
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <label htmlFor="endDate" className="text-sm font-medium">End Date</label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange(startDate, e.target.value)}
                className="w-full"
              />
            </div>

            {/* Period for Retention */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Retention Period</label>
              <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Site Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Site</label>
              <Select
                value={siteId?.toString() || ''}
                onValueChange={handleSiteChange}
                disabled={isLoadingSites}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sitesData.items?.map((site: Site) => (
                    <SelectItem key={site.id} value={site.id.toString()}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-end gap-2">
              <Button onClick={handleRefresh} disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
              <Button onClick={handleExport} variant="outline" disabled={isLoading || lifetimeData.length === 0}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Customers</p>
                <p className="text-xl font-semibold tracking-tight">{activeCustomers}</p>
                <p className="text-xs text-muted-foreground">of {totalCustomers} total</p>
              </div>
              <Users className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Customer Value</p>
                <p className="text-xl font-semibold tracking-tight">{formatCurrency(avgCustomerValue)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Retention Rate</p>
                <p className="text-xl font-semibold tracking-tight">{retentionRate.toFixed(1)}%</p>
              </div>
              {retentionRate >= 70 ? (
                <TrendingUp className="h-8 w-8 text-success" />
              ) : (
                <TrendingDown className="h-8 w-8 text-destructive" />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Outstanding</p>
                <p className="text-xl font-semibold tracking-tight">{formatCurrency(totalOutstanding)}</p>
              </div>
              <CreditCard className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Customer Overview</TabsTrigger>
          <TabsTrigger value="retention">Retention Analysis</TabsTrigger>
          <TabsTrigger value="credit">Credit Management</TabsTrigger>
          <TabsTrigger value="patterns">Order Patterns</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Customer Lifetime Metrics</CardTitle>
              <CardDescription>
                Detailed view of customer activity and value
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search Bar */}
              <div className="mb-4">
                <Input
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-sm"
                />
              </div>

              {/* Customer Table */}
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : filteredCustomers.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-center">Orders</TableHead>
                        <TableHead className="text-right">Total Spent</TableHead>
                        <TableHead className="text-right">Avg Order</TableHead>
                        <TableHead className="text-center">Frequency</TableHead>
                        <TableHead className="text-center">Last Order</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCustomers.map((customer) => (
                        <TableRow key={customer.party_id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{customer.party_name}</div>
                              <div className="text-sm text-muted-foreground">{customer.party_code}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusColor(customer.customer_status)}>
                              {customer.customer_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{customer.total_orders}</TableCell>
                          <TableCell className="text-right">{formatCurrency(customer.total_spent)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(customer.avg_order_value)}</TableCell>
                          <TableCell className="text-center">
                            {customer.order_frequency_days ? 
                              `${Math.round(customer.order_frequency_days)} days` : 
                              'N/A'
                            }
                          </TableCell>
                          <TableCell className="text-center">
                            <div>
                              <div className="text-sm">{customer.days_since_last_order} days ago</div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(customer.last_order_date), 'dd MMM')}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {customer.outstanding_amount > 0 && (
                              <span className="text-warning font-medium">
                                {formatCurrency(customer.outstanding_amount)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedCustomerId(customer.party_id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No customer data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="retention" className="space-y-4">
          {/* Retention Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Retention Trends</CardTitle>
              <CardDescription>Track retention and churn rates over time</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : retentionData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={retentionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="retention_rate" stroke="#10b981" name="Retention Rate (%)" strokeWidth={3} />
                      <Line yAxisId="left" type="monotone" dataKey="churn_rate" stroke="#ef4444" name="Churn Rate (%)" strokeWidth={2} />
                      <Line yAxisId="right" type="monotone" dataKey="avg_customer_value" stroke="#3b82f6" name="Avg Customer Value" strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No retention data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Flow Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Flow Analysis</CardTitle>
              <CardDescription>New vs returning customers over time</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : retentionData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={retentionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="new_customers" stackId="a" fill="#10b981" name="New Customers" />
                      <Bar dataKey="returning_customers" stackId="a" fill="#3b82f6" name="Returning Customers" />
                      <Bar dataKey="lost_customers" fill="#ef4444" name="Lost Customers" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credit" className="space-y-4">
          {/* Credit Distribution Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Outstanding Credit Distribution</CardTitle>
              <CardDescription>Customer credit exposure by range</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : creditData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pie Chart */}
                  <ChartContainer config={{}} className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={creditData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.credit_range}: ${entry.customer_count}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="total_outstanding"
                        >
                          {creditData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartContainer>

                  {/* Credit Table */}
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Credit Range</TableHead>
                          <TableHead className="text-center">Customers</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-center">% of Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creditData.map((item, index) => (
                          <TableRow key={item.credit_range}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                />
                                {item.credit_range}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div>
                                <div>{item.customer_count}</div>
                                <div className="text-xs text-muted-foreground">
                                  {(item.percentage_of_customers || 0).toFixed(1)}%
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(item.total_outstanding)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Progress 
                                value={item.percentage_of_outstanding} 
                                className="h-2"
                              />
                              <div className="text-xs text-muted-foreground mt-1">
                                {(item.percentage_of_outstanding || 0).toFixed(1)}%
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No credit data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-4">
          {/* Order Patterns Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Order Patterns</CardTitle>
              <CardDescription>Customer ordering behavior by day of week</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : orderPatternData.length > 0 ? (
                <ChartContainer config={{}} className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={orderPatternData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day_name" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="order_count" fill="#3b82f6" name="Order Count" />
                      <Bar yAxisId="left" dataKey="unique_customers" fill="#10b981" name="Unique Customers" />
                      <Line yAxisId="right" type="monotone" dataKey="avg_order_value" stroke="#f59e0b" name="Avg Order Value" strokeWidth={2} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No order pattern data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Order Distribution</CardTitle>
              <CardDescription>Percentage of orders by day</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : orderPatternData.length > 0 ? (
                <div className="space-y-4">
                  {orderPatternData.map((day) => (
                    <div key={day.day_of_week} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{day.day_name}</span>
                        <span className="text-sm text-muted-foreground">
                          {day.order_count} orders ({(day.percentage_of_orders || 0).toFixed(1)}%)
                        </span>
                      </div>
                      <Progress value={day.percentage_of_orders} className="h-3" />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>{day.unique_customers} customers</span>
                        <span>{formatCurrency(day.total_value || 0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomerId} onOpenChange={() => setSelectedCustomerId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Customer Product Preferences</DialogTitle>
            <DialogDescription>
              Top products purchased by this customer
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingPreferences ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : productPreferences.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Orders</TableHead>
                    <TableHead className="text-right">Total Qty</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                    <TableHead className="text-center">Last Ordered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productPreferences.map((pref) => (
                    <TableRow key={pref.item_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{pref.item_name}</div>
                          <div className="text-sm text-muted-foreground">{pref.item_code}</div>
                        </div>
                      </TableCell>
                      <TableCell>{pref.category_name || 'N/A'}</TableCell>
                      <TableCell className="text-center">{pref.order_count}</TableCell>
                      <TableCell className="text-right">
                        <div>
                          <div>{pref.total_quantity}</div>
                          <div className="text-sm text-muted-foreground">
                            Avg: {(pref.avg_quantity_per_order || 0).toFixed(1)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(pref.total_value)}</TableCell>
                      <TableCell className="text-center">
                        {format(new Date(pref.last_ordered), 'dd MMM yyyy')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No product preference data available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerMetricsPage;